import {
  Box,
  Checkbox,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { CheckboxOutlineIcon, CheckedIcon, DropdownIcon } from "app/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { CHECKBOX_SIZE, sanitizeModelFormValue } from "../../../hooks/useAddModelModal";
import { selectManufacturerModelForm } from "../../../redux/selector";
import { manufacturerModelActions } from "../../../redux/slice";
import { ManufacturerModelForm } from "../../../types";
import { useStyles } from "./styles";

const MANUFACTURER_MODELS_MODAL = "MANUFACTURER_MODELS.MODAL";

const FIELDS: Array<{
  key: keyof ManufacturerModelForm;
  label: string;
  type: "select" | "text";
  inputType?: "string" | "number";
  wide?: boolean;
}> = [
  { key: "manufacturerID", label: "MANUFACTURER", type: "select" },
  { key: "year", label: "YEAR", type: "select", inputType: "number" },
  { key: "modelDesc", label: "MODEL_DESCRIPTION", type: "text", inputType: "string" },
  { key: "licenseWeight", label: "LICENSED_WEIGHT", type: "text", inputType: "number" },
  { key: "frontExtension", label: "FRONT_OVERHANG", type: "text", inputType: "number" },
  { key: "weelDrive", label: "DRIVE", type: "select", inputType: "string" },
  { key: "baseBeamTypeRoof", label: "WHEELBASE", type: "select", inputType: "string" },
  { key: "frontWeelBase", label: "FRONT_AXLE_DISTANCE", type: "text", inputType: "number" },
  { key: "chassisWeight", label: "CHASSIS_WEIGHT", type: "text", inputType: "number" },
  { key: "baseBeamType", label: "OPENING_BASE", type: "select", inputType: "string" },
  { key: "chassisWidth", label: "CHASSIS_WIDTH", type: "text", inputType: "number" },
  { key: "chassisHeight", label: "CHASSIS_HEIGHT", type: "text", inputType: "number" },
  { key: "maxWidth", label: "MAX_WIDTH", type: "text", inputType: "number", wide: true },
  {
    key: "registrationInstruction",
    label: "REGISTRATION_INSTRUCTION",
    type: "text",
    inputType: "string",
    wide: true,
  },
];

const CHECKBOX_FIELDS: Array<{
  key: keyof ManufacturerModelForm;
  label: string;
}> = [
  { key: "sideLighting", label: "SIDE_LIGHTING" },
  { key: "sideGuard", label: "SIDE_GUARD" },
  { key: "mudguard", label: "MUDGUARD" },
  { key: "extendableChassis", label: "EXTENDABLE_CHASSIS" },
];

const ModelDetailsForm = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const form = useSelector(selectManufacturerModelForm);

  const set = (name: keyof ManufacturerModelForm, value: string | boolean) =>
    dispatch(
      manufacturerModelActions.setFormField({
        name,
        value: sanitizeModelFormValue(name, value),
      }),
    );

  const fieldPlaceholder = (label: string, type: "select" | "text") =>
    t(
      `${MANUFACTURER_MODELS_MODAL}.PLACEHOLDERS.${type === "select" ? "SELECT" : "ENTER"}_${label}`,
    );

  return (
    <Box className={styles.root}>
      <Box className={styles.grid}>
        {FIELDS.map(({ key, label, type, wide, inputType }) => (
          <Box
            key={key}
            className={`${styles.field}${wide ? ` ${styles.fieldWide}` : ""}`}
          >
            <Typography className={styles.label}>
              {t(`${MANUFACTURER_MODELS_MODAL}.FIELDS.${label}`)}
            </Typography>
            <TextField
              className={styles.input}
              fullWidth
              select={type === "select"}
              variant="outlined"
              placeholder={fieldPlaceholder(label, type)}
              value={String(form[key] || "")}
              InputProps={{ notched: false }}
              {...(inputType === "number" ? { inputMode: "numeric" as const } : {})}
              {...(type === "select"
                ? {
                    SelectProps: {
                      displayEmpty: true,
                      IconComponent: DropdownIcon,
                      renderValue: (v) => String(v || "") || fieldPlaceholder(label, "select"),
                    },
                  }
                : {})}
              onChange={(e) => set(key, e.target.value)}
            >
              {type === "select" ? (
                <MenuItem value="">{fieldPlaceholder(label, "select")}</MenuItem>
              ) : null}
            </TextField>
          </Box>
        ))}
      </Box>

      <Box className={styles.checks}>
        {CHECKBOX_FIELDS.map(({ key, label }) => (
          <Box
            key={key}
            className={styles.checkCard}
            onClick={() => set(key, !Boolean(form[key]))}
          >
            <Checkbox
              checked={Boolean(form[key])}
              disableRipple
              onChange={(e) => set(key, e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              icon={
                <CheckboxOutlineIcon
                  width={CHECKBOX_SIZE}
                  height={CHECKBOX_SIZE}
                />
              }
              checkedIcon={
                <CheckedIcon width={CHECKBOX_SIZE} height={CHECKBOX_SIZE} />
              }
            />
            <Typography className={styles.checkLabel}>
              {t(`${MANUFACTURER_MODELS_MODAL}.FIELDS.${label}`)}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box className={styles.notesField}>
        <Typography className={styles.label}>
          {t(`${MANUFACTURER_MODELS_MODAL}.FIELDS.NOTES`)}
        </Typography>
        <TextField
          className={styles.notesInput}
          fullWidth
          multiline
          variant="outlined"
          placeholder={fieldPlaceholder("NOTES", "text")}
          value={form.notes}
          InputProps={{ notched: false }}
          onChange={(e) => set("notes", e.target.value)}
        />
      </Box>
    </Box>
  );
};

export default ModelDetailsForm;
